import React, { useContext, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import SopotakContext from "../../../store/sopotak-context";
import config from "../../../api/api-helper";
import { getLocations, getRoutes } from "../../../api/api-helper";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import "./SearchWidget.css";
import {
  Container,
  FloatingLabel,
  Row,
  Col,
  ToggleButton,
  ToggleButtonGroup,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../styles.css";
import MapComponent from "../../map/MapComponent";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

const SearchWidget = () => {
  const ctx = useContext(SopotakContext);

  const { t, i18n } = useTranslation();

  const navigate = useNavigate();

  const maxPax = 7;

  const [locations, setLocations] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [ready, setReady] = useState(false);
  const [validated, setValidated] = useState(false);

  const [fromCoordinates, setFromCoordinates] = useState(null);
  const [toCoordinates, setToCoordinates] = useState(null);
  const [rotate, setRotate] = useState(false);

  const langParam = searchParams.get("lang");

  const swapFromTo = () => {
    if (ctx.from === ctx.to || !ctx.from || !ctx.to) return;

    // Start animation
    setRotate(true);

    // Swap destinations
    const newFrom = ctx.to;
    const newTo = ctx.from;

    ctx.setFromDestination(newFrom);
    ctx.setToDestination(newTo);

    clearOffers();

    // End animation after 400ms
    setTimeout(() => setRotate(false), 400);
  };

  const fetchOffers = (body) => {
    ctx.setOffersData(null);
    setIsLoading(true);

    const {
      type,
      departureLocation,
      departureTime,
      arrivalLocation,
      returnTime,
      numberOfPersons,
      numberOfChildren,
      numberOfBikes,
      durationInMinutes,
    } = body;

    const params = new URLSearchParams({
      type,
      departureLocation,
      departureTime: DateTime.fromJSDate(departureTime).toUTC().toISO(),
      arrivalLocation,
      numberOfPersons: numberOfPersons.toString(),
      numberOfChildren: numberOfChildren.toString(),
      numberOfBikes: numberOfBikes.toString(),
    });

    if (type === "TIME_BASED") {
      params.set("durationInMinutes", durationInMinutes.toString());
    }

    if (type === "RETURN") {
      params.set("returnTime", DateTime.fromJSDate(returnTime).toUTC().toISO());
    }

    const url = `${config.apiUrl}/api/sopotak/v1/offers?${params.toString()}`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        setIsLoading(false);
        ctx.setOffersData(data);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error fetching offers:", error);
      });
  };

  useEffect(() => {
    let mounted = true;
    getLocations().then((locationsResponse) => {
      if (mounted) {
        const parsedLocations = locationsResponse.locations.map((location) => ({
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude),
          value: location.value,
          label: location.label,
        }));
        setLocations(parsedLocations);
      }
    });
    getRoutes().then((routesResponse) => {
      if (mounted) {
        setRoutes(routesResponse.routes);
      }
    });
    return () => (mounted = false);
  }, []);

  useEffect(() => {
    if (langParam && langParam !== i18n.language) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  useEffect(() => {
    if (ready) return;

    const warsawNow = DateTime.now()
      .setZone("Europe/Warsaw")
      .set({ hour: 12, minute: 0, second: 0, millisecond: 0 });

    const searchTripType = searchParams.get("type") ?? ctx.tripType;
    const searchFrom = searchParams.get("depLoc") ?? ctx.from;
    const searchTo = searchParams.get("arrLoc") ?? ctx.to;
    const searchPersons = parseInt(searchParams.get("person") ?? ctx.persons);
    const searchChildren = parseInt(
      searchParams.get("children") ?? ctx.children
    );
    const searchBikes = parseInt(searchParams.get("bikes") ?? ctx.bikes);
    const searchDuration =
      validateDuration(searchParams.get("dur")) ?? ctx.duration;

    // WAŻNE: odczytaj daty z URL lub fallback na teraz
    const fromDateRaw = searchParams.get("depTime");
    const toDateRaw = searchParams.get("returnTime");

    const fromDate = fromDateRaw
      ? DateTime.fromISO(fromDateRaw, { zone: "Europe/Warsaw" }).set({
          hour: 12,
          minute: 0,
        })
      : warsawNow;

    const toDate = toDateRaw
      ? DateTime.fromISO(toDateRaw, { zone: "Europe/Warsaw" }).set({
          hour: 12,
          minute: 0,
        })
      : warsawNow;

    // Ustal ostateczne daty
    const finalFromDate = fromDate < warsawNow ? warsawNow : fromDate;
    const finalToDate = toDate < finalFromDate ? finalFromDate : toDate;

    const finalArrivalLocation = searchTripType === "TIME_BASED" ? searchFrom : searchTo;

    // Ustaw do kontekstu
    ctx.setTripType(searchTripType);
    ctx.setFromDestination(searchFrom);
    ctx.setToDestination(finalArrivalLocation);
    ctx.setFromDate(finalFromDate.toJSDate());
    ctx.setToDate(finalToDate.toJSDate());
    ctx.setPersons(searchPersons);
    ctx.setChildren(searchChildren);
    ctx.setBikes(searchBikes);
    ctx.setDuration(searchDuration);

    // Od razu wywołujemy fetchOffers – już z gotowymi datami
    const body = {
      type: searchTripType,
      departureLocation: searchFrom,
      departureTime: finalFromDate.toJSDate(),
      arrivalLocation: finalArrivalLocation,
      numberOfPersons: searchPersons,
      numberOfChildren: searchChildren,
      numberOfBikes: searchBikes,
      durationInMinutes: searchDuration,
      returnTime: finalToDate.toJSDate(),
    };

    if (!searchFrom || !finalArrivalLocation) return;
    fetchOffers(body);
    setReady(true);
  }, [ready, searchParams]);

  useEffect(() => {
    const fromLocation = locations.find(
      (location) => location.value === ctx.from
    );
    const toLocation = locations.find((location) => location.value === ctx.to);

    if (fromLocation) {
      setFromCoordinates({ lat: fromLocation.lat, lng: fromLocation.lng });
    } else {
      setFromCoordinates(null);
    }

    if (toLocation) {
      setToCoordinates({ lat: toLocation.lat, lng: toLocation.lng });
    } else {
      setToCoordinates(null);
    }
  }, [ctx.from, ctx.to, locations]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity()) {
      event.stopPropagation();
      return;
    }

    const body = {
      type: ctx.tripType,
      departureLocation: ctx.from,
      departureTime: ctx.fromDate,
      arrivalLocation: ctx.tripType === "TIME_BASED" ? ctx.from : ctx.to,
      numberOfPersons: ctx.persons,
      numberOfChildren: ctx.children,
      numberOfBikes: ctx.bikes,
      durationInMinutes: ctx.duration,
      returnTime: ctx.toDate,
    };

    fetchOffers(body);

    const params = new URLSearchParams({
      type: ctx.tripType,
      depLoc: ctx.from,
      depTime: DateTime.fromJSDate(ctx.fromDate).toUTC().toISO(),
      person: ctx.persons.toString(),
      children: ctx.children.toString(),
      bikes: ctx.bikes.toString(),
    });

    if (ctx.tripType === "RETURN") {
      params.set("arrLoc", ctx.to);
      params.set("returnTime", DateTime.fromJSDate(ctx.toDate).toUTC().toISO());
    } else if (ctx.tripType === "ONEWAY") {
      params.set("arrLoc", ctx.to);
    } else if (ctx.tripType === "TIME_BASED") {
      params.set("arrLoc", ctx.from);
      params.set("dur", ctx.duration.toString());
    }

    const lang = i18n.language;
    params.set("lang", lang);

    navigate(`/order?${params.toString()}`);
    setValidated(true);
  };

  function validateDuration(duration) {
    const parsedDuration = parseInt(duration);

    if (
      Number.isNaN(parsedDuration) ||
      parsedDuration % 60 !== 0 ||
      parsedDuration > 360 ||
      parsedDuration === 0
    ) {
      return null;
    }

    return parsedDuration;
  }

  const clearOffers = () => {
    ctx.setOffersData(null);
  };

  const fromDestinationHandler = (event) => {
    if (ctx.tripType === "TIME_BASED") {
      ctx.setToDestination(event.target.value);
    }
    ctx.setFromDestination(event.target.value);
    clearOffers();
  };

  const toDestinationHandler = (event) => {
    ctx.setToDestination(event.target.value);
    clearOffers();
  };

  const durationHandler = (event) => {
    ctx.setDuration(parseInt(event.target.value));
    clearOffers();
  };

  const personsHandler = (event) => {
    ctx.setPersons(parseInt(event.target.value));
    clearOffers();
  };

  const childrenHandler = (event) => {
    ctx.setChildren(parseInt(event.target.value));
    clearOffers();
  };

  const bikesHandler = (event) => {
    ctx.setBikes(parseInt(event.target.value));
    clearOffers();
  };

  const fromDateHandler = (e) => {
    const warsawDate = DateTime.fromISO(e.target.value, {
      zone: "Europe/Warsaw",
    }).set({ hour: 12, minute: 0 });
    ctx.setFromDate(warsawDate.toJSDate());

    if (DateTime.fromJSDate(ctx.toDate) < warsawDate) {
      ctx.setToDate(warsawDate.toJSDate());
    }

    clearOffers();
  };

  const toDateHandler = (e) => {
    const warsawDate = DateTime.fromISO(e.target.value, {
      zone: "Europe/Warsaw",
    }).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });

    ctx.setToDate(warsawDate.toJSDate());
    clearOffers();
  };

  const handleTripType = (value) => {
    if (value === "TIME_BASED") {
      // W trybie time-based to == from
      ctx.setToDestination(ctx.from);
    } else {
      // Dla innych trybów, jeśli from == to, wyczyść to
      if (ctx.from === ctx.to) {
        ctx.setToDestination("");
      }
    }
  
    ctx.setTripType(value);
    clearOffers();
  };

  const convertDate = (date) => {
    return DateTime.fromJSDate(date, { zone: "Europe/Warsaw" }).toFormat(
      "yyyy-MM-dd"
    );
  };

  const checkIfRouteExists = (departureLocation, arrivalLocation) => {
    return (
      routes.some(
        (route) =>
          route.departureLocation === departureLocation &&
          route.arrivalLocation === arrivalLocation
      ) ||
      !departureLocation ||
      !arrivalLocation
    );
  };

  const generateOptions = (
    currentValue,
    otherValuesSum,
    maxValue,
    includeZero = false
  ) => {
    const options = Array.from({ length: maxValue }, (_, i) => i + 1);
    if (includeZero) options.unshift(0);
    return options.map((value) => (
      <option
        key={value}
        value={value}
        disabled={value + otherValuesSum > maxPax}
      >
        {value}
      </option>
    ));
  };

  const selectedLocations = [];
  if (fromCoordinates) selectedLocations.push(fromCoordinates);
  if (toCoordinates && ctx.tripType !== "TIME_BASED")
    selectedLocations.push(toCoordinates);

  return (
    <div className="form-wrapper">
      <Form noValidate onSubmit={handleSubmit}>
        <Container fluid>
          <Row>
            <Col className="gx-2 mb-2">
              <ToggleButtonGroup
                type="radio"
                name="option"
                onChange={handleTripType}
                value={ctx.tripType}
                defaultValue="TIME_BASED"
                className="btn-group d-flex w-100"
              >
                <ToggleButton
                  variant="outline-dark"
                  value="TIME_BASED"
                  id="radio1"
                  className="w-100"
                >
                  {t("searchWidget.timeBased")}
                </ToggleButton>
                <ToggleButton
                  variant="outline-dark"
                  value="RETURN"
                  id="radio2"
                  className="w-100"
                >
                  {t("searchWidget.roundTrip")}
                </ToggleButton>
                <ToggleButton
                  variant="outline-dark"
                  value="ONEWAY"
                  id="radio3"
                  className="w-100"
                >
                  {t("searchWidget.oneWay")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Col>
          </Row>
          <Row className="align-items-center">
            <Col className="gx-2 mb-2">
              <Form.Group>
                <FloatingLabel
                  controlId="departurePort"
                  label={t("searchWidget.from")}
                >
                  <Form.Select
                    className="form-control"
                    size="lg"
                    onChange={fromDestinationHandler}
                    value={ctx.from}
                    required
                    isInvalid={validated && ctx.from.length < 1}
                  >
                    <option></option>
                    {locations.map((item) => (
                      <option
                        key={item.value}
                        value={item.value}
                        disabled={!checkIfRouteExists(item.value, ctx.to)}
                      >
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {t("searchWidget.required")}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            {/* BUTTON IN THE MIDDLE */}
            {(ctx.tripType === "RETURN" || ctx.tripType === "ONEWAY") && (
              <Col xs="auto" className="d-flex align-items-stretch mb-2 gx-2">
                <Button
                  className={`btn btn-primary swap-button ${
                    rotate ? "rotate" : ""
                  }`}
                  onClick={swapFromTo}
                >
                  <img
                    src="icons8-expand-arrows-48.png"
                    alt="swap"
                    style={{
                      width: "24px",
                      height: "24px",
                      objectFit: "contain",
                    }}
                  />
                </Button>
              </Col>
            )}
            {(ctx.tripType === "RETURN" || ctx.tripType === "ONEWAY") && (
              <Col className="gx-2 mb-2">
                <Form.Group>
                  <FloatingLabel
                    controlId="arrivalPort"
                    label={t("searchWidget.to")}
                  >
                    <Form.Select
                      className="form-control"
                      size="lg"
                      onChange={toDestinationHandler}
                      value={ctx.to}
                      placeholder="Wybierz"
                      required
                      isInvalid={validated && ctx.to.length < 1}
                    >
                      <option></option>
                      {locations.map((item) => (
                        <option
                          key={item.value}
                          value={item.value}
                          disabled={!checkIfRouteExists(ctx.from, item.value)}
                        >
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {t("searchWidget.required")}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            )}
            {ctx.tripType === "TIME_BASED" && (
              <Col className="col-6 gx-2 mb-2">
                <Form.Group>
                  <FloatingLabel
                    controlId="duration"
                    label={t("searchWidget.duration")}
                  >
                    <Form.Select
                      className="form-control"
                      size="lg"
                      onChange={durationHandler}
                      value={ctx.duration}
                      required
                      isInvalid={validated && ctx.duration.length < 1}
                    >
                      <option value={60}>1h</option>
                      <option value={120}>2h</option>
                      <option value={180}>3h</option>
                      <option value={240}>4h</option>
                      <option value={300}>5h</option>
                      <option value={360}>6h</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {t("searchWidget.required")}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            )}
          </Row>
          <Row>
            <Col className="col-6 gx-2 mb-2">
              <Form.Group>
                <FloatingLabel
                  controlId="departureDate"
                  label={t("searchWidget.departure")}
                >
                  <Form.Control
                    size="lg"
                    type="date"
                    min={DateTime.now()
                      .setZone("Europe/Warsaw")
                      .toFormat("yyyy-MM-dd")}
                    onChange={fromDateHandler}
                    value={convertDate(new Date(ctx.fromDate))}
                    required
                    isInvalid={validated && ctx.fromDate.length < 1}
                  />
                  <Form.Control.Feedback type="invalid">
                    {t("searchWidget.required")}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            {ctx.tripType === "RETURN" && (
              <Col className="col-6 gx-2 mb-2">
                <Form.Group>
                  <FloatingLabel
                    controlId="returnDate"
                    label={t("searchWidget.return")}
                  >
                    <Form.Control
                      size="lg"
                      type="date"
                      min={DateTime.fromJSDate(ctx.fromDate, {
                        zone: "Europe/Warsaw",
                      }).toFormat("yyyy-MM-dd")}
                      onChange={toDateHandler}
                      value={convertDate(new Date(ctx.toDate))}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {t("searchWidget.required")}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            )}
          </Row>
          <Row>
            <Col xs={12} md={4} className="gx-2 mb-2">
              <Form.Group>
                <FloatingLabel
                  controlId="persons"
                  label={t("searchWidget.persons")}
                >
                  <Form.Select
                    size="lg"
                    onChange={personsHandler}
                    value={ctx.persons}
                    required
                    isInvalid={validated && ctx.persons < 1}
                  >
                    {generateOptions(
                      ctx.persons,
                      ctx.children + ctx.bikes,
                      maxPax
                    )}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {t("searchWidget.adultRequired")}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12} md={4} className="gx-2 mb-2">
              <Form.Group>
                <FloatingLabel
                  controlId="children"
                  label={t("searchWidget.children")}
                >
                  <Form.Select
                    size="lg"
                    onChange={childrenHandler}
                    value={ctx.children}
                    required
                    isInvalid={validated && ctx.children < 0}
                  >
                    {generateOptions(
                      ctx.children,
                      ctx.persons + ctx.bikes,
                      maxPax - 1,
                      true
                    )}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {t("searchWidget.required")}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col xs={12} md={4} className="gx-2 mb-2">
              <Form.Group>
                <FloatingLabel
                  controlId="bikes"
                  label={t("searchWidget.bikes")}
                >
                  <Form.Select
                    size="lg"
                    onChange={bikesHandler}
                    value={ctx.bikes}
                    required
                    isInvalid={validated && ctx.bikes < 0}
                  >
                    {generateOptions(
                      ctx.bikes,
                      ctx.persons + ctx.children,
                      2,
                      true
                    )}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {t("searchWidget.required")}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col className="col-12 d-grid gap-2 gx-2">
              <Button
                size="lg"
                type="submit"
                disabled={isLoading}
                className="custom-button"
              >
                {isLoading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    role="status"
                    aria-hidden="true"
                    variant="light"
                  />
                ) : (
                  t("searchWidget.search")
                )}
              </Button>
            </Col>
          </Row>
          <Row>
            <Col className="gx-2 mt-2">
              <MapComponent
                allLocations={locations}
                selectedLocations={selectedLocations}
              />
            </Col>
          </Row>
        </Container>
      </Form>
    </div>
  );
};

export default SearchWidget;
